import { ApolloCache, gql } from '@apollo/client';

/**
 * Fields on the `Dashboard` type whose text the backend translates via
 * `$employer->getLocale()` (`DashboardResolver` and `Service/Dashboard/Recommenders/*`).
 * Only these go stale when the user switches language — the backend returns the rest of
 * the dashboard (layout, newsFeed, whatsNew, respondsToAppsBadge) the same either way.
 *
 * `adPackageRecommender` covers all five recommender widgets at once: an evict without
 * `args` removes every variant of the field, and so every `recommendationType`.
 */
const LOCALIZED_DASHBOARD_FIELDS = [
  'unreadApplications',
  'applicationCount',
  'jobViews',
  'adPackageRecommender',
];

/** Read from the cache only — this never goes to the network. */
const CACHED_DASHBOARD_ID = gql`
  query cachedDashboardId {
    dashboard {
      id
    }
  }
`;

/**
 * Evicts the dashboard fields the backend translates, so the widgets reading them fetch
 * again. Passed as `updateCache` to `client.refetchQueries` — Apollo then refetches
 * exactly those active queries whose data this evict changed.
 */
export const evictLocalizedDashboardFields = (cache: ApolloCache<unknown>): void => {
  const cached = cache.readQuery<{ dashboard?: { id: string } | null }>({
    query: CACHED_DASHBOARD_ID,
  });
  const dashboardId = cached?.dashboard?.id;

  if (!dashboardId) return;

  const id = cache.identify({ __typename: 'Dashboard', id: dashboardId });
  LOCALIZED_DASHBOARD_FIELDS.forEach((fieldName) => cache.evict({ id, fieldName }));
};
